import apiClient from '../index';

/**
 * Creates a new event
 * @param {Object} eventData - The event data to create
 * @returns {Promise<Object>} The created event
 */
export const createEvent = async (eventData) => {
  try {
    // Ensure all required fields are present and properly formatted
    const payload = {
      eventTitle: eventData.eventTitle?.trim(),
      eventDate: eventData.eventDate,
      endDate: eventData.endDate || eventData.eventDate,
      startTime: eventData.startTime,
      assignee: eventData.assignee?.trim(),
      endTime: eventData.endTime,
      eventType: eventData.eventType?.trim(),
      recurring: Boolean(eventData.recurring),
      recurrenceRule: eventData.recurring ? eventData.recurrenceRule : null,
      location: eventData.location?.trim(),
      description: eventData.description?.trim() || 'No description provided',
      participants: Array.isArray(eventData.participants)
        ? eventData.participants
        : [],
      classEvent: eventData.classEvent,
      classId: eventData.classId,
      subjectId: eventData.subjectId,
      termId: eventData.termId,
      sessionId: eventData.sessionId,
    };

    // Validate that no required fields are empty strings or null
    const requiredFields = [
      'eventTitle',
      'eventDate',
      'endDate',
      'startTime',
      'endTime',
      'location',
      'description',
      'eventType',
    ];
    const emptyFields = requiredFields.filter((field) => !payload[field]);

    if (emptyFields.length > 0) {
      throw new Error(
        `Required fields cannot be empty: ${emptyFields.join(', ')}`
      );
    }

    const response = await apiClient.post('/api/event', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating event:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Gets all events
 * @returns {Promise<Array>} List of events
 */
export const getEvents = async () => {
  try {
    const response = await apiClient.get('/api/event');
    return response.data;
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

/**
 * Gets all events version 2 - This is updated to combine events with recurring events
 * @returns {Promise<Array>} List of events
 */
export const getEventsV2 = async (startDate, endDate, staffId) => {
  try {
    const response = await apiClient.get(
      `/api/event/range?start=${startDate}&end=${endDate}&id=${staffId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

/**
 * Gets all events in the class schedule
 * @returns {Promise<Array>} List of events
 */
export const getClassEvents = async (startDate, endDate, classId) => {
  try {
    const response = await apiClient.get(
      `/api/event/classrange?start=${startDate}&end=${endDate}&id=${classId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching class schedule: ', error);
    throw error;
  }
};

/**
 * Gets all upcoming events
 * @returns {Promise<Array>} List of upcoming events
 */
export const getUpcomingEvents = async () => {
  try {
    const response = await apiClient.get('/api/event/upcoming');
    return response.data;
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

/**
 * Gets all upcoming events v2 - This one gets even recurring events for up to a week
 * @returns {Promise<Array>} List of upcoming events
 */
export const getUpcomingEventsV2 = async (id) => {
  try {
    const response = await apiClient.get(`/api/event/participant/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

/**
 * Gets an event by ID
 * @param {string} id - The event ID
 * @returns {Promise<Object>} The event details
 */
export const getEventById = async (id) => {
  try {
    const response = await apiClient.get(`/api/event/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching event with id ${id}:`, error);
    throw error;
  }
};

/**
 * Updates an existing event
 * @param {string} id - The event ID
 * @param {Object} eventData - The updated event data
 * @returns {Promise<Object>} The updated event
 */
export const updateEvent = async (id, eventData) => {
  try {
    const payload = {
      name: eventData.eventTitle,
      startDate: eventData.eventDate,
      endDate: eventData.endDate || eventData.eventDate,
      startTime: eventData.startTime,
      endTime: eventData.endTime,
      type: eventData.eventType,
      recurring: eventData.recurring,
      recurrenceRule: eventData.recurring ? eventData.recurrenceRule : null,
      location: eventData.location,
      description: eventData.description || '',
      participants: eventData.participants || [],
    };

    const response = await apiClient.put(`/api/event/${id}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating event with id ${id}:`, error);
    throw error;
  }
};

/**
 * Deletes an event
 * @param {string} id - The event ID
 * @returns {Promise<Object>} Response data
 */
export const deleteEvent = async (id) => {
  try {
    const response = await apiClient.delete(`/api/event/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting event with id ${id}:`, error);
    throw error;
  }
};
